import { Warning } from "@mui/icons-material";
import { LinearProgress, Stack, Typography } from "@mui/material";
import { Timer } from "@pages/OrderTracker/components/PlanningAutomation/Timer";
import {
  AutomationStatus,
  getStatusColour,
} from "@pages/OrderTracker/components/PlanningAutomation/utils";
import { OrderSearchOut } from "@providers/hop-ord-server/api";
import { toLocaleDateTimeString } from "@utils";
import dayjs from "dayjs";
import AutomationStatusIcon from "./AutomationStatusIcon";
import { useMemo } from "react";

interface Props {
  order: OrderSearchOut;
}
const AutomationInProgress = (props: Props) => {
  const planGenerationAttempt = props.order.latestPlanGenerationAttempt;
  const status = planGenerationAttempt?.status;

  const warnings = planGenerationAttempt?.warnings?.length || 0;
  const planProgressPercentage =
    planGenerationAttempt?.planProgressPercentage ?? 0;

  const completedTimeInLocale = useMemo(() => {
    if (planGenerationAttempt?.result?.finishedAt) {
      return toLocaleDateTimeString(
        planGenerationAttempt?.result?.finishedAt || "",
      );
    }
    return "";
  }, [planGenerationAttempt]);

  const linearProgressColor = useMemo(() => {
    return getStatusColour(status as AutomationStatus);
  }, [status]);

  const startTime = dayjs(planGenerationAttempt?.planningStartedAt);

  const completedTime = dayjs(planGenerationAttempt?.result?.finishedAt);

  const planName = planGenerationAttempt?.relatedPrescription;

  return (
    <Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1,
          }}
        >
          <AutomationStatusIcon
            status={status as AutomationStatus}
            fontSize="20px"
          />
          <Typography
            variant="body2"
            noWrap
            color={status === "error" ? "error.dark" : "text.primary"}
            sx={{
              alignItems: "center",
              textOverflow: "ellipsis",
              maxWidth: "390px",
            }}
          >
            {planName || props.order.orderName}
          </Typography>

          {warnings > 0 && <Warning color="warning" fontSize="small" />}
        </Stack>
      </Stack>

      <LinearProgress
        sx={{
          marginBottom: 1,
          marginTop: 1,
          ...(status === "error" && {
            "& .MuiLinearProgress-bar": {
              backgroundColor: "error.dark",
            },
          }),
        }}
        variant="determinate"
        value={planProgressPercentage}
        color={linearProgressColor}
      />
      <Stack direction="row">
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        ></Typography>

        {status === "not_started" && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Queued
          </Typography>
        )}
        {status === "error" && (
          <Typography
            variant="body2"
            sx={{
              color: "error.dark",
            }}
          >
            Failed: {completedTimeInLocale}
          </Typography>
        )}
        {status === "in_progress" && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {(planGenerationAttempt?.currentStepIndex || 0) + 1}/
            {planGenerationAttempt?.stepSequence?.length}{" "}
            {planGenerationAttempt?.currentStep}
          </Typography>
        )}
        {status === "success" && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Completed:
            </Typography>{" "}
            &nbsp;
            <Timer startTime={startTime} completedTime={completedTime} />
          </>
        )}
      </Stack>
    </Stack>
  );
};
export default AutomationInProgress;
